
import { buildModelArray } from "@/common/utils/misc";

const KEY_CODE = {
	BACKSPACE: 8,
	TAB: 9,
	ENTER: 13,
	SHIFT: 16,
	SPACEBAR: 32,
	LEFT: 37,
	RIGHT: 39,
	DELETE: 46,
};

const LETTER_RANGE_A_Z = {
	START: 65,
	END: 90,
};

export default {
	props: {
		value: {
			required: true,
			type: Array,
		},
		id: {
			required: false,
			type: String,
			default: null,
		},
	},
	data() {
		return {
			localValue: buildModelArray(this.value.length, this.value),
			inputWidth: 48,
			inputMargin: 6,
			endIconWidth: 24,
			endIconMargin: this.isExtraLargeDevice ? 8 : 4,
			inputsPerRow: 6,
		};
	},
	computed: {
		inputCount() {
			return this.value.length;
		},
		hasInput() {
			return this.localValue.some((val) => val);
		},
	},
	watch: {
		inputCount() {
			this.localValue = buildModelArray(this.value.length, [
				...this.localValue,
			]);
		},
		localValue() {
			this.$emit("input", this.localValue);
		},
	},
	mounted() {
		this.inputsPerRow = this.calculateNumberOfInputsPerRow();
		const maxWidth = this.calculateMaxWidth();
		if (maxWidth) {
			this.$el.style.maxWidth = maxWidth;
		}
	},
	methods: {
		onKeyUp(e, index) {
			if (this.isLastTile(index)) {
				return;
			}

			if (
				(this.isTileFilledIn(index) && this.isLetter(e.keyCode)) ||
				(!this.isTileFilledIn(index) && this.isSpacebar(e.keyCode))
			) {
				this.focusToInput(index + 1);
			}
		},
		onKeyDown(e, index) {
			if (e.keyCode === KEY_CODE.BACKSPACE) {
				if (this.isFirstTile(index)) {
					return;
				}

				if (!this.isTileFilledIn(index)) {
					this.focusToInput(index - 1);
				}
			} else if (
				!this.isLetter(e.keyCode) &&
				!this.isTabOrShift(e.keyCode) &&
				!this.isEnter(e.keyCode) &&
				!this.isArrowKey(e.keyCode) &&
				!this.isDeleteKey(e.keyCode)
			) {
				e.preventDefault();
			}
		},
		onClearButtonClick() {
			this.localValue = buildModelArray(this.value.length);
		},
		focusToInput(index) {
			const input = this.$refs[`input-${index}`];
			const el = input?.[0]?.$el;
			el.focus?.();
			el.select?.();
		},
		isTileFilledIn(index) {
			return this.localValue[index] !== "";
		},
		isFirstTile(index) {
			return index <= 0;
		},
		isLastTile(index) {
			return index >= this.localValue.length - 1;
		},
		isLetter(keyCode) {
			return (
				keyCode >= LETTER_RANGE_A_Z.START &&
				keyCode <= LETTER_RANGE_A_Z.END
			);
		},
		isSpacebar(keyCode) {
			return keyCode === KEY_CODE.SPACEBAR;
		},
		isTabOrShift(keyCode) {
			return keyCode === KEY_CODE.TAB || keyCode === KEY_CODE.SHIFT;
		},
		isEnter(keyCode) {
			return keyCode === KEY_CODE.ENTER;
		},
		isArrowKey(keyCode) {
			return keyCode === KEY_CODE.LEFT || keyCode === KEY_CODE.RIGHT;
		},
		isDeleteKey(keyCode) {
			return keyCode === KEY_CODE.DELETE;
		},
		onlyOneLetter(v) {
			if (v?.[0]?.length === 1 && v[0].match(/[a-z]/i)) {
				return v[0];
			}

			return "";
		},
		shouldShowHyphenAtIndex(index = 0) {
			if (this.isExtraLargeDevice) {
				// We only want to show it on the mobile layout.
				return false;
			}

			const entryCount = this.localValue.length;

			if ((index + 1) % this.inputsPerRow !== 0) {
				// This is not the correct index.
				return false;
			}

			if (entryCount > index + 1) {
				return true;
			}

			return false;
		},
		calculateNumberOfInputsPerRow() {
			// Window width, minus horzontal padding values.
			const width = this.$el?.getBoundingClientRect?.().width || 0;

			const inputWidthWithMargin = this.inputWidth + this.inputMargin;

			const endWidth = this.endIconWidth + this.endIconMargin;

			// We will always have at least 3 inputs.
			const base = this.inputWidth + inputWidthWithMargin * 2 + endWidth;
			let remainingSpace = width - base;
			remainingSpace = Math.max(remainingSpace, 0);

			const additionalInputs = Math.floor(
				remainingSpace / inputWidthWithMargin
			);

			return 3 + additionalInputs;
		},
		calculateMaxWidth() {
			if (this.isExtraLargeDevice) {
				return null;
			}
			const inputWidthWithMargin = this.inputWidth + this.inputMargin;
			const endWidth = this.endIconWidth + this.endIconMargin;

			const maxWidth =
				this.inputWidth +
				inputWidthWithMargin * (this.inputsPerRow - 1) +
				endWidth;

			return `${maxWidth}px`;
		},
	},
};
